import { FC, useState } from 'react'
import { Box, Typography, ClickAwayListener, SelectProps } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import theme from 'theme'

/* Utils */
import useSearchLabel from 'hooks/useSearchLabel'
import { categoryToUrl, urlToCategoryCapitalized } from 'ui/pages/library/utils'
import { formatCPIDisplayNameForURL } from 'lib/utils'
import { CPILibraryItem, IChartDataCategory } from 'models'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'

/* Components */
import { Wrapper, Popper } from './cpiNavigation.styles'
import { CpiNavigationHoverTooltip } from 'ui/components/tooltip/LibraryDetailsTooltip'
import CpiNavigationSearch from './CpiNavigationSearch'
import { formatCpiName } from '../utils'
import { DevicesTabs } from '../../../useLibraryDetailsData'

interface Props extends SelectProps {
  options: CPILibraryItem[] | IChartDataCategory[]
  version: string
  callback?: () => void
  cpiName: string
  handlePredictionModalClose?: (hideNotification?: boolean) => void
}

const CpiNavigation: FC<Props> = ({ options, cpiName, version, callback, handlePredictionModalClose, ...rest }) => {
  const { setRenderingChart, setInitialLoad, setTabValue } = useLibraryDetails()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [popperOpen, setPopperOpen] = useState(false)
  const navigate = useNavigate()

  const convertedOptions = options
    .map((item) => {
      if (version === 'cpi' && 'display_name' in item && 'title' in item) {
        return {
          label: item.display_name,
          name: item.title
        }
      } else if ('score' in item) {
        return {
          label: urlToCategoryCapitalized(item.category),
          name: urlToCategoryCapitalized(item.category)
        }
      }
      return { label: '', name: '' }
    })
    .sort((a, b) => a.label.localeCompare(b.label))

  const { search, setSearch, labelOptions, highlightMatch } = useSearchLabel(convertedOptions, null, true)

  return (
    <ClickAwayListener
      onClickAway={() => {
        setPopperOpen(false)
        setAnchorEl(null)
      }}
    >
      <Wrapper className="cpi-navigation-wrapper">
        <Box className="cpi-name-wrapper" onClick={() => setPopperOpen(!popperOpen)}>
          <Typography className="navigation-cpi-name" ref={(elm) => setAnchorEl(elm)}>
            {formatCpiName(cpiName)}
          </Typography>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: popperOpen ? 'rotate(180deg)' : '',
              transition: 'all .2s ease-in-out'
            }}
          >
            <path
              d="M9.75 4.125L6 7.875L2.25 4.125"
              stroke={theme.colors.textPrimary}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        <Popper id="cpi-navigation-popper" open={popperOpen} anchorEl={anchorEl} placement="bottom-end">
          <Box className="content">
            <CpiNavigationSearch search={search} setSearch={setSearch} />

            <Box className="list-wrapper">
              <Box className="list">
                {labelOptions.map((option) => (
                  <CpiNavigationHoverTooltip
                    title={version === 'cpi' ? option.label + ' ' + option.name : option.label}
                    placement="right"
                    key={option.label}
                    PopperProps={{
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 8]
                          }
                        }
                      ]
                    }}
                  >
                    <Box
                      className="list-item"
                      onClick={() => {
                        const newCpi = formatCPIDisplayNameForURL(option.label)

                        if (!rest.multiple) {
                          setAnchorEl(null)
                        }
                        if (setInitialLoad) {
                          setInitialLoad(false)
                        }
                        if (setRenderingChart) {
                          setRenderingChart(true)
                        }
                        if (callback) {
                          callback()
                        }
                        if (handlePredictionModalClose) {
                          handlePredictionModalClose(true)
                        }

                        // Since the useEffect in useLibraryDetailsData will handle the redirect
                        // to the correct tab based on active status, we need to set the tab value
                        // and navigate to ensure both URL and tab state are updated
                        if (version === 'cpi') {
                          // Set tab to trends initially - the useEffect will correct if needed
                          setTabValue(DevicesTabs.Trends)
                          navigate(`/library/cpis/${newCpi}/trends`)
                        } else {
                          // Category navigation
                          navigate(`/library/categories/${categoryToUrl(option.label)}`)
                        }
                      }}
                    >
                      <Typography className="list-item-text">
                        {highlightMatch(version === 'cpi' ? option.label + ' ' + option.name : option.label)}
                      </Typography>
                    </Box>
                  </CpiNavigationHoverTooltip>
                ))}
              </Box>
            </Box>
          </Box>
        </Popper>
      </Wrapper>
    </ClickAwayListener>
  )
}

export default CpiNavigation
